import React, { useState, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import {
    intl,
    DmsModal,
    DmsButton,
    fetchData,
    HTTP_STATUS,
} from "dmsbasereact";
import {
    Box,
    Grid,
    FormControlLabel,
    Checkbox,
    Typography,
    Card,
    CardContent,
    IconButton,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Swal from "sweetalert2";
import { DmsAutocomplete } from "../DmsAutoComplete/DmsAutoComplete";
import { formatCnpj } from "../../utils/formatters";
import { getInstitutionCategoryLabel } from "../../constants/InstitutionType";

export const PartnerLinkModal = ({ isVisible, onClose, onSave }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const [isPublic, setIsPublic] = useState(false);
    const [institutionPks, setInstitutionPks] = useState([]);
    const [institutionCnpjPks, setInstitutionCnpjPks] = useState([]);
    const [cnpjOptions, setCnpjOptions] = useState([]);
    const [distributorPks, setDistributorPks] = useState([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const fetchCnpjOptions = async () => {
            try {
                const response = await fetchData("/institution/all/approved?category=101,102,104&is_public=false", "GET", true);
                const data = response.data.data.rows || response.data.data;
                if (Array.isArray(data)) {
                    const options = data.map((item) => ({
                        label: formatCnpj(item.organization_branch?.cnpj || ""),
                        value: item.organization_branch_pk,
                        name: item.organization_branch?.name || item.name || "",
                        categories: item.category_values || [],
                    }));
                    setCnpjOptions(options);
                }
            } catch (error) {
                console.error("Error fetching CNPJ options:", error);
            }
        };

        if (isVisible && !isPublic) {
            fetchCnpjOptions();
        }
    }, [isVisible, isPublic]);

    const selectedInstitutions = cnpjOptions.filter((opt) => institutionCnpjPks.includes(opt.value));

    const handleRemoveInstitution = useCallback((valueToRemove) => {
        setInstitutionCnpjPks((prev) => prev.filter((v) => v !== valueToRemove));
    }, []);

    const handlePublicChange = useCallback((e) => {
        setIsPublic(e.target.checked);
        setInstitutionPks([]);
        setInstitutionCnpjPks([]);
        setDistributorPks([]);
    }, []);

    const handleClose = useCallback(() => {
        setIsPublic(false);
        setInstitutionPks([]);
        setInstitutionCnpjPks([]);
        setDistributorPks([]);
        onClose();
    }, [onClose]);

    const handleSave = useCallback(async () => {
        if (isPublic && institutionPks.length === 0) {
            Swal.fire({
                icon: "warning",
                title: intl.get("MissingInformation").d("Missing Information"),
                text: intl.get("SelectAtLeastOneInstitution").d("Please select at least one institution."),
            });
            return;
        }

        if (!isPublic && institutionCnpjPks.length === 0) {
            Swal.fire({
                icon: "warning",
                title: intl.get("MissingInformation").d("Missing Information"),
                text: intl.get("SelectAtLeastOneInstitution").d("Please select at least one institution."),
            });
            return;
        }

        if (distributorPks.length === 0) {
            Swal.fire({
                icon: "warning",
                title: intl.get("MissingInformation").d("Missing Information"),
                text: intl.get("SelectAtLeastOneDistributor").d("Please select at least one distributor."),
            });
            return;
        }

        setSaving(true);
        try {
            const finalInstitutionPks = isPublic ? institutionPks : institutionCnpjPks;
            const payload = {
                is_public: isPublic,
                institution_pks: finalInstitutionPks,
                distributor_pks: distributorPks,
            };

            const response = await fetchData("/partner/link", "POST", true, payload);

            if (response.status === HTTP_STATUS.CREATED_201 || response.status === HTTP_STATUS.OK_200) {
                Swal.fire({
                    icon: "success",
                    title: intl.get("Success").d("Success"),
                    text: intl.get("PartnerLinkCreatedSuccessfully").d("Partner link created successfully."),
                });
                if (onSave) onSave();
                handleClose();
            } else {
                Swal.fire({
                    icon: "error",
                    title: intl.get("Error").d("Error"),
                    text: response.data?.detail || intl.get("FailedToCreatePartnerLink").d("Failed to create partner link."),
                });
            }
        } catch (error) {
            console.error("Error creating partner link:", error);
            Swal.fire({
                icon: "error",
                title: intl.get("Error").d("Error"),
                text: intl.get("FailedToCreatePartnerLink").d("Failed to create partner link."),
            });
        } finally {
            setSaving(false);
        }
    }, [isPublic, institutionPks, institutionCnpjPks, distributorPks, onSave, handleClose]);

    const institutionUrl = isPublic
        ? "/institution/all/approved?category=101,102,104&is_public=true"
        : "/institution/all/approved?category=101,102,104&is_public=false";

    const distributorUrl = isPublic
        ? "/institution/all/approved?category=100&is_public=true"
        : "/institution/all/approved?category=100&is_public=false";

    return (
        <DmsModal
            isVisible={isVisible}
            onClose={handleClose}
            fullScreen={isMobile}
            maxWidth="sm"
            fullWidth
        >
            <DmsModal.Header
                title={intl.get("CreateDistributorClientLink").d("Create Distributor Client Link")}
                onClose={handleClose}
            />
            <DmsModal.Body>
                <Box sx={{ p: isMobile ? 1 : 2 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={isPublic}
                                        onChange={handlePublicChange}
                                    />
                                }
                                label={intl.get("IsPublicInstitution").d("Is Public Institution")}
                            />
                        </Grid>
                        {isPublic ? (
                            <Grid item xs={12}>
                                <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1 }}>
                                    {intl.get("InstitutionName").d("Institution Name")}
                                </Typography>
                                <DmsAutocomplete
                                    key={`institution-public-${isPublic}`}
                                    label={intl.get("SelectInstitutions").d("Select Institutions")}
                                    name="institution_pks"
                                    multiple={true}
                                    url={institutionUrl}
                                    properties={{ label: "name", value: "organization_branch_pk" }}
                                    value={institutionPks}
                                    onChange={setInstitutionPks}
                                />
                            </Grid>
                        ) : (
                            <Grid item xs={12}>
                                <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1 }}>
                                    {intl.get("CNPJ").d("CNPJ")}
                                </Typography>
                                <DmsAutocomplete
                                    key={`cnpj-select-${institutionCnpjPks.join(",")}`}
                                    label={intl.get("SelectByCNPJ").d("Select by CNPJ")}
                                    name="institution_cnpj_pks"
                                    multiple={true}
                                    options={cnpjOptions}
                                    value={institutionCnpjPks}
                                    onChange={setInstitutionCnpjPks}
                                />
                                {selectedInstitutions.length > 0 && (
                                    <Box
                                        sx={{
                                            mt: 1,
                                            maxHeight: 200,
                                            overflowY: "auto",
                                            border: "1px solid",
                                            borderColor: "grey.300",
                                            borderRadius: 1,
                                            p: 1,
                                        }}
                                    >
                                        <Grid container spacing={2}>
                                            {selectedInstitutions.map((inst) => (
                                                <Grid item xs={12} sm={6} key={inst.value}>
                                                    <Card
                                                        variant="outlined"
                                                        sx={{
                                                            backgroundColor: "grey.100",
                                                        }}
                                                    >
                                                        <CardContent sx={{ py: 1.5, px: 2, "&:last-child": { pb: 1.5 } }}>
                                                            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
                                                                <Box sx={{ pr: 1, flex: 1 }}>
                                                                    <Typography
                                                                        variant="body2"
                                                                        sx={{ fontWeight: "bold", wordBreak: "break-word" }}
                                                                    >
                                                                        {inst.name}
                                                                    </Typography>
                                                                    <Typography
                                                                        variant="caption"
                                                                        color="text.secondary"
                                                                        sx={{ display: "block" }}
                                                                    >
                                                                        {inst.label}
                                                                    </Typography>
                                                                    {inst.categories?.length > 0 && (
                                                                        <Typography
                                                                            variant="caption"
                                                                            color="text.secondary"
                                                                        >
                                                                            {inst.categories.map((cat) => getInstitutionCategoryLabel(cat)).join(", ")}
                                                                        </Typography>
                                                                    )}
                                                                </Box>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => handleRemoveInstitution(inst.value)}
                                                                    sx={{ flexShrink: 0, mt: -0.5 }}
                                                                >
                                                                    <CloseIcon fontSize="small" />
                                                                </IconButton>
                                                            </Box>
                                                        </CardContent>
                                                    </Card>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </Box>
                                )}
                            </Grid>
                        )}
                        <Grid item xs={12}>
                            <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1 }}>
                                {intl.get("Distributor").d("Distributor")}
                            </Typography>
                            <DmsAutocomplete
                                key={`distributor-public-${isPublic}`}
                                label={intl.get("SelectDistributors").d("Select Distributors")}
                                name="distributor_pks"
                                multiple={true}
                                url={distributorUrl}
                                properties={{ label: "name", value: "organization_branch_pk" }}
                                value={distributorPks}
                                onChange={setDistributorPks}
                            />
                        </Grid>
                    </Grid>
                </Box>
            </DmsModal.Body>
            <DmsModal.Footer>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: isMobile ? "column" : "row",
                        justifyContent: "flex-end",
                        gap: 1,
                        width: "100%",
                        p: isMobile ? 1 : 2,
                    }}
                >
                    <DmsButton
                        variant="secondary"
                        text={intl.get("Cancel").d("Cancel")}
                        onClick={handleClose}
                        fullWidth={isMobile}
                        disabled={saving}
                    />
                    <DmsButton
                        variant="primary"
                        text={saving ? intl.get("Saving").d("Saving...") : intl.get("Save").d("Save")}
                        onClick={handleSave}
                        fullWidth={isMobile}
                        disabled={saving}
                    />
                </Box>
            </DmsModal.Footer>
        </DmsModal>
    );
};

PartnerLinkModal.propTypes = {
    isVisible: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func,
};

export default PartnerLinkModal;
